import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Flex, Table, Tbody, Td, Tooltip } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { addGoalsToGlobalGoalsMap } from 'actions/globalGoalsMapActions';
import { StyledAvatar } from 'components/avatars';
import useLocalStorage from 'components/hooks/useLocalStorage';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { format } from 'date-fns';
import Task1 from 'models/task1';
import { useDispatch, useSelector } from 'react-redux';
import { StyledNodesView } from 'screens/common/sidepanel/sidepanelStyles';
import { getDaysAgo } from 'screens/oneOnOne/landingPage/LandingPageV2/DateHelpers';
import { selectGoalAndMetadataFromGlobalGoals } from 'selectors/globalGoalSelector';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { FlexContainer, PaddingCSS, StyledBox, StyledGrid, StyledTypography } from 'styles/common';
import { themeColors } from 'themes/clients/baseTheme';

import { BulletList, KeyResultIcon, KeyResultPrivate2, ObjectiveIcon } from 'assets/assetsV1/iconComponents';

import { ANALYTICS_TEXTS } from 'constants/ProjectConstants';
import { localStorageKeys } from 'constants/localStorageKeys';
import theme from 'constants/theme';

import { fetchGoals } from 'services/analytics/goal';
import { showTaskDetails } from 'services/taskDetails';

import { Button } from 'uiToolkitV1/Button';
import { TableBodyRow, TableFoot } from 'uiToolkitV1/TableComponents';
import { TablePagination as RabbitTablePagination } from 'uiToolkitV1/TableComponents/Pagination';
import { TableBodyCell } from 'uiToolkitV1/TableComponents/TableCells';
import { TableHead } from 'uiToolkitV1/TableComponents/TableHeader';
import { TextParseComponent } from 'uiToolkitV1/TextParseComponent';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { ErrorMessage, Loader, NoData } from '../../../../analytics/commonComponents';
import RabbitGoalProgressBar from '../Audit/RabbitGoalProgressBar';
import { GoalsTableHeaderCell as TableHeaderCell } from '../GoalsTableHeaderCell';
import { useNoCountPagination } from '../useNoCountPagination';

const defaultRowsPerPage = 10;

const rowsPerPageOptions = [10, 20, 30];

const noDataText = 'This list is empty! Try changing the time period.';

const getFlexJustifyFromCellAlign = (align) => {
  switch (align) {
    case 'left':
      return 'flex-start';
    case 'center':
      return 'center';
    case 'right':
      return 'flex-end';
    default:
      return 'center';
  }
};

const columnList = [
  {
    id: 'TEXT',
    title: 'Goals',
    justify: 'flex-start',
    fixedWidth: '25rem',
    isSortable: true,
    tooltip: '',
    component: (goal) => {
      const isStrikedThroughStatus =
        (goal?.goalClosureStatus === 'DROPPED' ||
          goal?.goalClosureStatus === 'ACHIEVED' ||
          goal?.goalClosureStatus === 'NOT_ACHIEVED') &&
        goal?.status === 'COMPLETED';
      return (
        <Flex p="8px" pl={1}>
          <TextBodyMedium
            wordBreak
            textDecorationColor="textPrimary"
            color={theme.palette.text.heading}
            display="flex"
            truncate
          >
            <Tooltip label={goal.text.length > 120 ? goal.text : ''}>
              <Flex alignItems="flex-start">
                {/* <RealignHoverIconForGoals onRealignClick={onRealignClick} /> */}
                {goal?.visibilityType === 'PRIVATE' ? (
                  <Flex pt="2px">
                    <KeyResultPrivate2 color={themeColors.functional['4657AE']} boxSize="24px" />
                  </Flex>
                ) : (
                  <Flex>
                    <KeyResultIcon color={themeColors.functional['4657AE']} boxSize="24px" />
                  </Flex>
                )}

                <div
                  className="highlight-able-text"
                  style={{
                    marginLeft: '4px',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    webkitLineClamp: '2',
                    webkitBoxOrient: 'vertical',
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                  }}
                >
                  <TextParseComponent
                    isMultiLineTruncate
                    text={goal.text}
                    res={goal.macros}
                    isStrikedThrough={isStrikedThroughStatus}
                    isGreyedOut={goal?.goalClosureStatus === 'DROPPED'}
                    weight="inherit"
                    color="inherit"
                  />
                </div>
              </Flex>
            </Tooltip>
          </TextBodyMedium>
        </Flex>
      );
    },
  },
  {
    id: 'TAGS',
    title: 'Objective',
    fixedWidth: '20rem',
    justify: 'left',
    tooltip: '',
    component: (goal, handleObjectiveNameClick) => (
      <>
        {goal.subObjective ? (
          <Flex alignItems="flex-start" justifyContent="initial" flexWrap="nowrap">
            <Flex p="2px">
              <ObjectiveIcon color={themeColors.functional['7651BC']} boxSize="24px" />
            </Flex>

            <Flex direction="column" flex="1 1 auto">
              <div>
                <Flex alignItems="flex-start">
                  <TextBodyMedium
                    style={{
                      marginTop: '4px',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      webkitLineClamp: '2',
                      webkitBoxOrient: 'vertical',
                      wordBreak: 'break-word',
                      hyphens: 'auto',
                    }}
                  >
                    {goal.subObjective.displayName}
                  </TextBodyMedium>
                </Flex>
              </div>
            </Flex>
          </Flex>
        ) : (
          <EmptyBar />
        )}
      </>
    ),
  },
  {
    id: 'LAST_CHECKIN_TS',
    title: 'Last Check-in',
    fixedWidth: '15rem',
    justify: 'center',
    tooltip: '',
    component: (goal) => (
      <StyledGrid container justify="center">
        {goal.lastCheckInTS ? (
          <StyledBox padding="3.5rem 0" borderRadius={4}>
            <StyledTypography style={{ marginTop: '0.5rem' }} variant="body2" weight={400}>
              {/* {humanDate} */}
              {getDaysAgo(goal.lastCheckInTS)}
            </StyledTypography>
            <StyledTypography style={{ fontSize: '12px' }} variant="body2" color={theme.palette.grey.dark} weight={400}>
              {/* {`${convertDateToString7(goal.lastCheckInTS, true)},  ${
                    convertDateToString10(goal.lastCheckInTS).split(',')[1]
                  }`} */}
              {`${format(new Date(goal.lastCheckInTS), 'h a')} on ${format(new Date(goal.lastCheckInTS), 'MMM d')}`}
            </StyledTypography>
          </StyledBox>
        ) : (
          <EmptyBar />
        )}
      </StyledGrid>
    ),
  },
  {
    id: 'NAME',
    title: 'Owners',
    fixedWidth: '20rem',
    justify: 'center',
    isSortable: true,
    tooltip: '',
    component: (goal) => (
      <>
        {goal.toIds && goal.toIds.length > 0 ? (
          <StyledNodesView
            employees={goal.toIds}
            maxNodes={4}
            useEmployeeClick={false}
            renderAvatar={(props) => (
              <StyledAvatar
                {...props}
                size="smallest"
                leftGutter={-1}
                border={
                  props?.ownerType === Task1.EMPLOYEE_TYPES.owner
                    ? `2px solid ${theme.palette.green.main}`
                    : '2px solid #EAEAEA'
                }
              />
            )}
            onClick={(e) => {
              e.preventDefault();
            }}
          />
        ) : (
          <FlexContainer style={{ justifyContent: 'center' }}>
            <EmptyBar />
          </FlexContainer>
        )}
      </>
    ),
  },
  {
    id: 'GOAL_PROGRESS_PERCENTAGE',
    title: 'Progress',
    fixedWidth: '20rem',
    justify: 'flex-start',
    isSortable: true,
    tooltip: '',
    component: (goal, className) => (
      <RabbitGoalProgressBar
        goalProgressLevel={goal?.goalProgressLevel}
        percentageValue={Number(goal.getGoalProgress(0) || 0)}
      />
    ),
  },
];

export const defaultRowsPerPageForGoals = {
  [localStorageKeys.rabbitGoalsListTableRowsPerPage]: defaultRowsPerPage,
};

const TableHeader = ({ headers }) => (
  <TableHead>
    {headers.map((headerObj) => {
      const { id, title, fixedWidth, justify, isSortable, orderBy, handleSort } = headerObj;

      return (
        <TableHeaderCell
          key={id}
          py={2}
          px={3}
          width={fixedWidth}
          onClick={() => {
            if (!isSortable) return;
            handleSort(id, orderBy);
          }}
          withSorting={isSortable}
          sortOrder={id === orderBy.columnId ? orderBy.orderType : null}
          textProps={{ textAlign: justify }}
          style={isSortable ? { cursor: 'pointer' } : {}}
        >
          {title}
        </TableHeaderCell>
      );
    })}
  </TableHead>
);

const TableRow = ({ goal: localGoal, onRowClick = () => {} }) => {
  const globalGoalAndMetadata = useSelector(selectGoalAndMetadataFromGlobalGoals(localGoal?.id));

  if (globalGoalAndMetadata?.isDeleted) return null;

  const globalGoal = globalGoalAndMetadata?.data ?? localGoal;

  return (
    <StyledTableBodyRow key={globalGoal?.id} onClick={() => onRowClick(globalGoal)}>
      <>
        {columnList.map((colData, idx) => (
          <TableBodyCell
            key={colData.id}
            className="info"
            p={2}
            pl={idx === 0 ? 0 : 2}
            flexContainerProps={{ justifyContent: colData.justify }}
          >
            {colData.component(globalGoal, () => {})}
          </TableBodyCell>
        ))}
      </>

      <RowActions columnCount={columnList.length} />
    </StyledTableBodyRow>
  );
};

const RowActions = ({ columnCount }) => (
  <TableBodyCell colSpan={columnCount - 1} width="75rem" className="action" py={2} pl={0} pr={6}>
    <Flex width="100%" alignItems="center" justifyContent="flex-end">
      <Button variant="rowButtonOutlined" StartIcon={BulletList} startIconProps={{ fill: 'transparent' }}>
        View Details
      </Button>
    </Flex>
  </TableBodyCell>
);

const GoalsClickThroughTable = ({ selectedTimeBoundPeriods, selectedCohort, tagId, employeeId }) => {
  const [rowsPerPageLS, setRowsPerPageLS] = useLocalStorage(
    localStorageKeys.rabbitGoalsListTableRowsPerPage,
    defaultRowsPerPageForGoals
  );
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const dispatch = useDispatch();

  const { page, rowsPerPage, hasMore, updatePage, updateHasMore, updateRowsPerPage } = useNoCountPagination({
    defaultRowsPerPage: rowsPerPageLS[localStorageKeys.rabbitGoalsListTableRowsPerPage],
    initialPage: 0,
  });

  // const { setExpandedRowDetailsView, setNoData, setErrorData } = useGoalAnalytics();

  const [goals, setGoals] = useState([]);

  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [errorData, setErrorData] = useState(null);

  const [orderBy, setOrderBy] = useState({
    columnId: null,
    orderType: 'ASC',
    filterTagType: null,
  });

  const fetchData = useCallback(
    ({ selectedCohort, selectedTimeBoundPeriods, orderBy, page, rowsPerPage }) => {
      setNoData(false);
      setErrorData(false);

      setLoading(true);

      fetchGoals({
        limit: rowsPerPage + 1,
        offset: page * rowsPerPage,
        cohort: selectedCohort,
        timeBounds: selectedTimeBoundPeriods,
        orderByWithFilterTagType: orderBy,
        isPrivateGoalRequired: true,
        tagId,
        employeeId,
      })
        .then((res) => {
          if (res.length === 0) {
            setNoData(true);
            return;
          }

          dispatch(addGoalsToGlobalGoalsMap({ goals: res }));

          if (res.length > rowsPerPage) {
            updateHasMore(true);
          }

          setGoals(res.slice(0, rowsPerPage));
        })
        .catch((err) => {
          console.error(err);
          setErrorData(true);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [updateHasMore]
  );

  useEffect(() => {
    if (selectedCohort && selectedTimeBoundPeriods) {
      fetchData({
        selectedCohort,
        selectedTimeBoundPeriods,
        orderBy,
        page,
        rowsPerPage,
      });
    }
  }, [selectedCohort, selectedTimeBoundPeriods, orderBy, page, rowsPerPage, fetchData]);

  const handleSort = useCallback(
    (id, orderBy) => {
      if (orderBy.columnId === id) {
        setOrderBy((prev) => ({
          ...prev,
          orderType: prev.orderType === 'ASC' ? 'DESC' : 'ASC',
        }));
      } else {
        setOrderBy({
          columnId: id,
          orderType: 'ASC',
          filterTagType: id,
        });
      }
    },
    [setOrderBy]
  );

  const mappedHeaders = useMemo(
    () =>
      columnList.map((headerObj) => {
        let { id, title, fixedWidth, justify, isSortable } = headerObj;

        if (title === 'Goals') title = moduleName;
        if (title === 'Objective') title = objectivesName;

        return {
          id,
          title,
          fixedWidth,
          justify,
          isSortable,
          orderBy,
          handleSort,
        };
      }),
    [orderBy, handleSort]
  );

  const openGoalPanel = useCallback(
    (goal) => {
      dispatch(showTaskDetails({ taskIds: [goal?.id], loggedInEmployee }));
    },
    [dispatch, loggedInEmployee]
  );

  const dataRows = useMemo(() => {
    if (!goals) return [];

    return goals.map((goal) => <TableRow key={goal?.id} goal={goal} onRowClick={openGoalPanel} />);
  }, [goals, openGoalPanel]);

  const renderTableFooter = () => (
    <TableFoot>
      <RabbitTablePagination
        count={-1}
        page={page}
        onPageChange={(_, pageNum) => updatePage(pageNum)}
        rowsPerPage={rowsPerPage}
        handleChangeRowsPerPage={({ target }) => {
          setRowsPerPageLS({
            ...rowsPerPageLS,
            [localStorageKeys.rabbitGoalsListTableRowsPerPage]: target.value,
          });
          updateRowsPerPage(target.value);
        }}
        rowsPerPageOptions={rowsPerPageOptions}
        labelDisplayedRows={() => {
          const startingPosition = page * rowsPerPage;
          return `${startingPosition + 1}-${startingPosition + Math.min(goals.length, rowsPerPage)}`;
        }}
        disableNextButton={!hasMore}
      />
    </TableFoot>
  );

  return (
    <>
      {loading ? (
        <Loader />
      ) : errorData ? (
        <ErrorMessage errorText={ANALYTICS_TEXTS.REVIEW_TABLE.ERROR_MSG} />
      ) : noData ? (
        <NoData infoText={noDataText} />
      ) : (
        <Box minW="100%" pt={2} px={3}>
          <Table>
            <TableHeader headers={mappedHeaders} />
            <Tbody>
              {dataRows.map((dataRow, index) => (
                <Fragment key={index}> {dataRow} </Fragment>
              ))}
            </Tbody>
            {renderTableFooter()}
          </Table>
        </Box>
      )}
    </>
  );
};

const StyledTableCell = styled(TableBodyCell)`
  position: relative;
  height: 68px;
  cursor: ${(props) => (props.isSortable ? 'pointer' : 'inherit')};
`;

const EmptyBar = styled.div`
  width: 30px;
  height: 8px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.neutral['999999']};
`;

const StyledTd = styled(Td)`
  border-bottom: none;
  cursor: ${(props) => (props.isSortable ? 'pointer' : 'inherit')};
  ${({ center }) =>
    center &&
    `
    text-align: center;
  `};
  ${PaddingCSS};
`;

export const StyledTableBodyRow = styled(TableBodyRow)`
  .action {
    display: none;
  }

  &:hover {
    cursor: pointer;
    & > .info:not(:first-child) {
      display: none;
    }

    & > td:first-of-type .highlight-able-text {
      color: ${(props) => props.theme.colors.text['1565BC']};
    }

    & > .action {
      display: table-cell;
    }
  }
`;

export default GoalsClickThroughTable;
